<script setup lang="ts">
import CloudAlertOutline from 'vue-material-design-icons/CloudAlertOutline.vue'
import { ref, watch } from 'vue'
import type { StoryMessageSave } from '@/features/story-conversation/surface'
const props = withDefaults(defineProps<{ save: StoryMessageSave; content: string; copyLabel?: string; disabled?: boolean }>(), {
  copyLabel: 'Copy response',
})
const emit = defineEmits<{ retry: [] }>()
const copying = ref(false)
const copied = ref(false)
const copyError = ref('')
watch(() => props.content, () => { copied.value = false; copyError.value = '' })

function retry() {
  if (!props.disabled && !props.save.busy) emit('retry')
}
async function copy() {
  if (props.disabled || copying.value) return
  copying.value = true
  copyError.value = ''
  copied.value = false
  try {
    await navigator.clipboard.writeText(props.content)
    copied.value = true
  } catch {
    copyError.value = 'Could not copy. Select the text and copy it manually.'
  } finally { copying.value = false }
}
</script>

<template>
  <details class="message-save" @keydown.esc="($event.currentTarget as HTMLDetailsElement).open = false">
    <summary :aria-label="save.busy ? 'Saving message' : 'Message save needs attention'" :title="save.busy ? 'Saving message' : 'Message save needs attention'">
      <CloudAlertOutline :size="17" :decorative="true" />
    </summary>
    <div class="message-save__popover">
      <p>{{ save.description ?? 'This response is kept on this device. Retry saves the same text without generating a new response.' }}</p>
      <div class="message-save__actions">
        <button type="button" :disabled="disabled || save.busy" @click="retry">{{ save.busy ? 'Saving…' : 'Retry save' }}</button>
        <button type="button" :disabled="disabled || copying" @click="copy">{{ copied ? 'Copied' : copyLabel }}</button>
      </div>
      <p v-if="copyError" role="alert" class="message-save__copy-error">{{ copyError }}</p>
      <span class="sr-only" role="status">{{ copied ? 'Text copied to clipboard.' : '' }}</span>
    </div>
  </details>
</template>

<style scoped>
.message-save { position: relative; display: inline-block; margin-top: .5rem; color: var(--theme-text-secondary); }
.message-save summary { display: inline-flex; cursor: pointer; color: var(--theme-danger); list-style: none; padding: .25rem; border-radius: .25rem; }
.message-save summary::-webkit-details-marker { display: none; }
.message-save__popover { position: absolute; z-index: 20; left: 0; bottom: 100%; width: min(18rem, 75vw); padding: .9rem; border-radius: .5rem; background: var(--theme-surface-solid); color: var(--theme-on-surface); box-shadow: 0 4px 18px #0003; }
.message-save__popover p { margin: 0 0 .65rem; font-size: .85rem; line-height: 1.5; }
.message-save__actions { display: flex; flex-wrap: wrap; gap: 1rem; }
.message-save__popover button { cursor: pointer; font-size: .85rem; text-decoration: underline; text-underline-offset: .2rem; }
.message-save__popover button:disabled { opacity: .5; cursor: default; }
.message-save__popover .message-save__copy-error { margin: .65rem 0 0; color: var(--theme-danger); }
</style>
